import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Database, ArrowDownToLine } from "lucide-react";

export default function Import() {
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState("clients");
  const [parsed, setParsed] = useState(null); // {signature, headers, rows}
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/import/entities").then((r) => setEntities(r.data)).catch(() => {}); }, []);
  const spec = entities.find((e) => e.key === entity);
  const reset = () => { setParsed(null); setReport(null); setResult(null); };

  const blobDownload = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    try { const res = await api.get("/import/template", { params: { entity }, responseType: "blob" });
      blobDownload(res.data, `estimate_${entity}_template.xlsx`);
    } catch { toast.error("Could not download template"); }
  };

  const exportData = async () => {
    try { const res = await api.get(`/export/${entity}`, { responseType: "blob" });
      blobDownload(res.data, `estimate_${entity}_export.xlsx`);
      toast.success("Exported current data");
    } catch { toast.error("Could not export"); }
  };

  const exportAll = async () => {
    try { const res = await api.get(`/export-all`, { responseType: "blob" });
      blobDownload(res.data, `estimate_all_export.xlsx`);
      toast.success("Exported all data to one workbook");
    } catch { toast.error("Could not export"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    reset(); setBusy(true);
    try {
      const fd = new FormData(); fd.append("entity", entity); fd.append("file", file);
      const res = await api.post("/import/parse", fd);
      setParsed({ signature: res.data.signature, headers: res.data.headers, rows: res.data.rows });
      setReport(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read that file"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/import/commit", { entity, ...parsed });
      setResult(res.data.imported);
      toast.success(`Imported ${res.data.imported} ${spec?.label || entity}`);
      setParsed(null); setReport(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8">
      <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
        <Database className="w-5 h-5 text-[#003E87]" /> Data import &amp; export
      </h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-6">
        Import your data as Excel (.xlsx). Easiest path: download the template, paste your rows, and upload it back. You can also upload your <b>own spreadsheet</b> — just make sure the first row is column headers (e.g. Name, Email). Columns are matched by name, so “Contact Name” maps to contact_name. Every row is validated on the way in.
      </p>

      <Card className="p-5 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={entity} onValueChange={(v) => { setEntity(v); reset(); }}>
            <SelectTrigger className="w-56" data-testid="import-entity"><SelectValue /></SelectTrigger>
            <SelectContent>
              {entities.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template">
            <Download className="w-4 h-4 mr-1.5" /> Download template
          </Button>
          <Button variant="outline" onClick={exportData} data-testid="export-data">
            <ArrowDownToLine className="w-4 h-4 mr-1.5" /> Export current data
          </Button>
          <Button variant="outline" onClick={exportAll} data-testid="export-all">
            <ArrowDownToLine className="w-4 h-4 mr-1.5" /> Export everything
          </Button>
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#003E87] text-white text-sm hover:bg-[#00305F] cursor-pointer" data-testid="import-file-label">
            <Upload className="w-4 h-4" /> {busy ? "Reading…" : "Upload filled .xlsx"}
            <input type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="import-file-input" disabled={busy} />
          </label>
          {result !== null && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Imported {result}</Badge>}
        </div>
        {spec && (
          <div className="mt-3 text-xs text-slate-400">
            Columns: {spec.columns.map((c) => `${c.name}${c.required ? "*" : ""}`).join(", ")} <span className="text-slate-300">(* required)</span>
          </div>
        )}
      </Card>

      {report?.fatal && (
        <Card className="p-4 mb-4 border-rose-200 bg-rose-50" data-testid="import-fatal">
          <div className="flex items-start gap-2 text-sm text-rose-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{report.fatal}</span>
          </div>
        </Card>
      )}

      {report && !report.fatal && (
        <Card className="p-4 mb-4" data-testid="import-report">
          {report.ok ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> {report.count} clean row{report.count === 1 ? "" : "s"} ready to import.
              </div>
              <Button onClick={commit} disabled={busy || report.count === 0} data-testid="run-import"
                className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Import {report.count} rows
              </Button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 text-sm text-amber-700 mb-3">
                <AlertTriangle className="w-4 h-4" /> {report.errors.length} problem(s) found. Fix these in your sheet and re-upload. {report.count} row(s) are already clean.
              </div>
              <div className="max-h-64 overflow-y-auto border border-slate-100 rounded">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-slate-100 text-slate-400 sticky top-0 bg-white">
                    <th className="text-left px-3 py-1.5 font-medium">Row</th>
                    <th className="text-left font-medium">Column</th>
                    <th className="text-left font-medium px-3">Problem</th></tr></thead>
                  <tbody>
                    {report.errors.map((er, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`error-row-${i}`}>
                        <td className="px-3 py-1 text-slate-600">{er.row}</td>
                        <td className="text-slate-700 font-medium">{er.column}</td>
                        <td className="px-3 text-rose-600">{er.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {parsed && (
        <Card className="overflow-x-auto">
          <div className="text-xs text-slate-400 px-3 pt-3">Preview (first 5 rows)</div>
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-100">{parsed.headers.map((c) => <th key={c} className="text-left px-3 py-2 font-medium text-slate-500">{c}</th>)}</tr></thead>
            <tbody>{parsed.rows.slice(0, 5).map((r, i) => (<tr key={i} className="border-b border-slate-50">{parsed.headers.map((c) => <td key={c} className="px-3 py-1.5 text-slate-600 truncate max-w-[160px]">{String(r[c] ?? "")}</td>)}</tr>))}</tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
